import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, HStack, Popover, Portal, Text, VStack } from '@chakra-ui/react';
import { LuChevronDown } from 'react-icons/lu';
import CountedLensFilter, { type CountedLensOption } from '@/components/common/CountedLensFilter';

interface ArchiveViewPickerProps<Value extends string> {
	value: Value;
	options: readonly CountedLensOption<Value>[];
	onChange: (value: Value) => void;
}

const widthOf = (element: Element): number => element.getBoundingClientRect().width;

/** Mobile scrolls the whole run; desktop overflows only the choices that do not fit. */
const ArchiveViewPicker = <Value extends string>({ value, options, onChange }: ArchiveViewPickerProps<Value>) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const measurementRef = useRef<HTMLDivElement>(null);
	const [visibleCount, setVisibleCount] = useState(options.length);
	const selectedOption = options.find((option) => option.value === value);

	useLayoutEffect(() => {
		const container = containerRef.current;
		const measurement = measurementRef.current;
		if (!container || !measurement) return;

		const measure = () => {
			const buttons = [...measurement.querySelectorAll('button')];
			if (buttons.length !== options.length + 2) return;

			const optionWidths = buttons.slice(0, options.length).map(widthOf);
			const moreWidth = widthOf(buttons[options.length]);
			const selectedWidth = widthOf(buttons[options.length + 1]);
			const optionGroup = measurement.querySelector('[role="group"]');
			const optionGap = optionGroup ? Number.parseFloat(getComputedStyle(optionGroup).columnGap) || 0 : 0;
			const outerGap = Number.parseFloat(getComputedStyle(measurement).columnGap) || 0;
			const available = container.getBoundingClientRect().width;
			const allWidth = optionWidths.reduce((sum, width) => sum + width, 0) + optionGap * (options.length - 1);

			if (allWidth <= available || options.length < 2) {
				setVisibleCount((current) => (current === options.length ? current : options.length));
				return;
			}

			// One hidden choice is more work than leaving its pill visible. If anything collapses,
			// put at least two choices behind the disclosure and keep the largest prefix that fits.
			const mostVisibleWithOverflow = Math.max(0, options.length - 2);
			let nextVisibleCount = 0;
			for (let count = mostVisibleWithOverflow; count >= 0; count -= 1) {
				const visibleWidth = optionWidths.slice(0, count).reduce((sum, width) => sum + width, 0);
				const selectedIsHidden = options.slice(count).some((option) => option.value === value);
				const overflowWidth = selectedIsHidden ? selectedWidth : moreWidth;
				const requiredWidth =
					visibleWidth + optionGap * Math.max(0, count - 1) + (count ? outerGap : 0) + overflowWidth;
				if (requiredWidth <= available) {
					nextVisibleCount = count;
					break;
				}
			}
			setVisibleCount((current) => (current === nextVisibleCount ? current : nextVisibleCount));
		};

		measure();
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		observer.observe(measurement);
		return () => observer.disconnect();
	}, [options, value]);

	const desktopOptions = options.slice(0, visibleCount);
	const overflowOptions = options.slice(visibleCount);
	const hasOverflowSelection = overflowOptions.some((option) => option.value === value);
	const overflowLabel = hasOverflowSelection ? selectedOption?.label : `${overflowOptions.length} more`;

	return (
		<>
			<Box display={{ base: 'block', md: 'none' }} overflowX="auto" py={1} my={-1}>
				<CountedLensFilter
					label="What to show in the archive"
					value={value}
					options={options}
					onChange={onChange}
					presentation="buttons"
					hideCountsWhenNarrow
				/>
			</Box>

			<Box position="relative">
				<HStack ref={containerRef} display={{ base: 'none', md: 'flex' }} gap={2} minW={0} w="full">
					{desktopOptions.length ? (
						<CountedLensFilter
							label="Visible archive views"
							value={value}
							options={desktopOptions}
							onChange={onChange}
							presentation="buttons"
						/>
					) : null}
					{overflowOptions.length ? (
						<Popover.Root positioning={{ placement: 'bottom-end' }}>
							<Popover.Trigger asChild>
								<Button
									type="button"
									size="sm"
									h={10}
									px={4}
									borderRadius="full"
									variant="ghost"
									layerStyle="control"
									fontWeight={hasOverflowSelection ? 'semibold' : 'medium'}
									color={hasOverflowSelection ? 'brand.contrast' : 'brand.fg'}
									bg={hasOverflowSelection ? 'brand.solid' : 'surface.default'}
									_hover={{ bg: hasOverflowSelection ? 'brand.solid' : 'surface.subtle' }}
									// No fade. Choosing a hidden view that then fits the row mounts its own pill
									// already selected while this button, the same element, would fade out of the
									// selected colour over it: two selected pills for a fifth of a second.
									transition="none"
									aria-pressed={hasOverflowSelection}
									aria-label={
										hasOverflowSelection
											? `Current archive view: ${selectedOption?.label}`
											: `${overflowOptions.length} more archive views`
									}
								>
									{overflowLabel}
									<LuChevronDown />
								</Button>
							</Popover.Trigger>
							<Portal>
								<Popover.Positioner>
									<Popover.Content
										w="240px"
										borderRadius="3xl"
										borderColor="border.default"
										boxShadow="lg"
										p={2}
									>
										<Popover.Body p={1}>
											<VStack align="stretch" gap={1}>
												{overflowOptions.map((option) => (
													<Popover.CloseTrigger key={option.value} asChild>
														<Button
															type="button"
															variant="ghost"
															justifyContent="space-between"
															borderRadius="xl"
															bg={
																value === option.value
																	? 'sidebar.active'
																	: 'transparent'
															}
															// Stated because the `bg` above outranks the ghost recipe's own
															// hover, which left the rows inert.
															_hover={{
																bg:
																	value === option.value
																		? 'sidebar.active'
																		: 'surface.subtle',
															}}
															aria-pressed={value === option.value}
															onClick={() => onChange(option.value)}
														>
															{option.label}
															<Text as="span" textStyle="micro" color="fg.subtle">
																{option.count}
															</Text>
														</Button>
													</Popover.CloseTrigger>
												))}
											</VStack>
										</Popover.Body>
									</Popover.Content>
								</Popover.Positioner>
							</Portal>
						</Popover.Root>
					) : null}
				</HStack>

				{/* A single unconstrained copy supplies the rendered widths. It is out of flow and
				    invisible, so measuring reserves no second row and exposes no duplicate controls. */}
				<HStack
					ref={measurementRef}
					position="absolute"
					top={0}
					left={0}
					gap={2}
					visibility="hidden"
					pointerEvents="none"
					w="max-content"
					aria-hidden="true"
				>
					<CountedLensFilter
						label="Measure archive views"
						value={value}
						options={options}
						onChange={onChange}
						presentation="buttons"
					/>
					<Button
						type="button"
						size="sm"
						h={10}
						px={4}
						borderRadius="full"
						fontWeight="semibold"
						tabIndex={-1}
					>
						{options.length} more
						<LuChevronDown />
					</Button>
					<Button
						type="button"
						size="sm"
						h={10}
						px={4}
						borderRadius="full"
						fontWeight="semibold"
						tabIndex={-1}
					>
						{selectedOption?.label}
						<LuChevronDown />
					</Button>
				</HStack>
			</Box>
		</>
	);
};

export default ArchiveViewPicker;
